import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import _ from 'lodash';
import { numberWithCommas } from 'spw-shared/utils';
import {
  CenteredSpaceBetweenRow,
  SpaceBetweenRow,
} from 'spw-shared/globalComponents/grid';
import { Box } from '@mui/material';
import {
  onBlurRatio,
  onBlurQuantity,
  onBlurUsf,
} from 'spw-shared/worksheet-calculation-utils';
import { MAX_DIM, MAX_QTY, MAX_USF } from 'spw-constants';
import { CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH } from 'spw-programming-projects/constants';
import {
  MultiplierBox,
  SpaceTypeNameContainer,
} from 'spw-components/worksheet/styles';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import {
  GridColumn,
  GridRow,
  RowTitle,
} from 'spw-programming-projects/building-details/worksheet/styles';
import { useSelector } from 'react-redux';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { MeasurementSystem, Unit } from 'spw-components/unit-conversion/types';
import WorksheetRow from '../../../../models/WorksheetRow';
import WorksheetStorageSpaceRow from '../../../../models/WorksheetStorageSpaceRow';
import WorksheetValuesInput from './worksheet-values-input';
import {
  isWholeNumber,
  limitInputValue,
  useRowSizeInputs,
  useSpaceTypeInputValue,
} from './utils';
import QuantityRoundingPopup from './quantity-rounding-popup';
import { useQuantityRounding } from './hooks';

export interface StorageSpaceTypeRowProps {
  changeChildRow?: (
    row: WorksheetRow,
    options?: { name?: string; isNameUpdate?: boolean }
  ) => void;
  totalWorkSeats: number;
  active?: boolean;
  disabled?: boolean;
  showDecimals: boolean;
  row: WorksheetStorageSpaceRow;
  isPerDepartmentRow?: boolean;
}

export const StorageSpaceTypeRow: React.FC<StorageSpaceTypeRowProps> = ({
  row,
  active,
  disabled,
  showDecimals,
  changeChildRow,
  totalWorkSeats,
  isPerDepartmentRow,
}) => {
  if (!row) return null;
  const rowRef = useRef(null);

  const testingSuffix = `child_${row.spaceType.id}`;

  const { name: spaceTypeName } = row.spaceType;

  const [editValue, setEditValue] = useState('');
  const [editFactor, setEditFactor] = useState('');
  const initialEditValue = useRef('');
  const [isDirty, setIsDirty] = useState(false);
  const [hover, setHover] = useState(false);
  const [name, setName] = useState(spaceTypeName);

  const { convertToUnit, convertToOriginalUnit } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);
  const quantityInputRef = React.useRef<HTMLInputElement>();
  const [roundingPopupAnchorEl, setRoundingPopupAnchorEl] = useState<
    HTMLInputElement
  >(null);

  const decimalsForDimensionsEnabled =
    measurementSystem === MeasurementSystem.imperial;

  useEffect(() => {
    setName(spaceTypeName);
  }, [spaceTypeName]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (editFactor !== '') {
      setIsDirty(true);
    }
  };

  let className = isPerDepartmentRow ? 'white-background' : '';
  if (editFactor !== '') {
    className = 'active';
  } else if (hover || active) {
    className = 'hover';
  }

  const { getSpaceTypeInputValue } = useSpaceTypeInputValue({
    showDecimals,
    rowName: name,
    editFactor,
    editValue,
  });

  const ratioValue = getSpaceTypeInputValue('ratio', row.ratio);
  const quantityValue = getSpaceTypeInputValue(
    'lockersOrDrawersQuantity',
    row.lockersOrDrawersQuantity
  );
  const usableSquareInchesValue = getSpaceTypeInputValue(
    'usableSquareInches',
    convertToUnit(row.usableSquareInches, {
      unitType: 'area',
      inputUnit: Unit.inches,
      outputUnit:
        measurementSystem === MeasurementSystem.imperial
          ? Unit.inches
          : Unit.meters,
    }) as number
  );

  const sizeXValue =
    getSpaceTypeInputValue(
      'sizeX',
      convertToUnit(row.sizeX, {
        unitType: 'linear',
        outputIsFractionUnit: true,
      }) as number,
      false,
      decimalsForDimensionsEnabled
    ) || '';
  const sizeYValue =
    getSpaceTypeInputValue(
      'sizeY',
      convertToUnit(row.sizeY, {
        unitType: 'linear',
        outputIsFractionUnit: true,
      }) as number,
      false,
      decimalsForDimensionsEnabled
    ) || '';

  const handleInputChange = (value: string, maxValue: number) => {
    setIsDirty(true);
    setEditValue(limitInputValue(value, maxValue));
  };

  const onFocusRowInput = useCallback(
    (
      rowField: keyof WorksheetStorageSpaceRow,
      event?: React.FocusEvent<HTMLInputElement>
    ) => {
      let newEditValue = '';
      if (event) {
        newEditValue = event.target.value
          ? event.target.value.replace(/,/g, '')
          : '';
      } else {
        newEditValue = row[rowField]
          ? (row[rowField] as number).toFixed(2)
          : '';
      }
      initialEditValue.current = newEditValue;
      setEditValue(newEditValue);
      setEditFactor(`${name}.${rowField}`);
    },
    [row, name]
  );

  const blurCurrentInput = useCallback(() => {
    setEditValue('');
    setEditFactor('');
    setIsDirty(false);
  }, []);

  const unitConvertedEditValue = useMemo(() => {
    return convertToOriginalUnit(parseFloat(editValue), {
      unitType: editFactor.includes('usableSquareInches') ? 'area' : 'linear',
      outputIsFractionUnit: true,
    }).toString();
  }, [editValue, convertToOriginalUnit, editFactor]);

  const { onBlurSizeInput } = useRowSizeInputs({
    editFactor,
    editValue: unitConvertedEditValue,
    row,
    usfFieldKey: 'usableSquareInches',
  });

  const handleSizeInputBlurred = useCallback(() => {
    const updatedRow = onBlurSizeInput();
    const hasRowValueChanged =
      updatedRow.sizeX !== row.sizeX || updatedRow.sizeY !== row.sizeY;
    if (hasRowValueChanged)
      changeChildRow(
        WorksheetStorageSpaceRow.toWorksheetRow(updatedRow, totalWorkSeats)
      );
    blurCurrentInput();
  }, [changeChildRow, blurCurrentInput, onBlurSizeInput, totalWorkSeats, row]);

  const { handleRoundQuantity } = useQuantityRounding({
    quantityInputRef,
    setEditValue,
    editValue,
  });

  if (row.removed) return <div />;

  const shouldShowUSFError =
    editFactor !== `${name}.lockersOrDrawersQuantity` &&
    parseInt(quantityValue, 10) > 0 &&
    parseFloat(usableSquareInchesValue) * 100 < 1;

  return (
    <div ref={rowRef}>
      <GridRow
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={className}
        data-cy={`worksheet_${row.child ? 'child_' : ''}row_${spaceTypeName}`}
        data-testid={`worksheet_row_${spaceTypeName}`}
      >
        <GridColumn xs={3} className="row-start">
          <SpaceBetweenRow style={{ width: '100%' }}>
            <Box key="title" width="calc(100% - 22px)" marginLeft="22px">
              {row.title ? (
                <RowTitle style={{ marginLeft: 8 }}>
                  <p>{row.title}</p>
                  <p className="subtitle">{row.subtitle}</p>
                </RowTitle>
              ) : (
                <SpaceTypeNameContainer
                  style={{
                    wordBreak: 'normal',
                    overflowWrap: 'anywhere',
                    maxWidth: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
                    cursor: 'default',
                  }}
                  data-testid={`worksheet_row_name_${testingSuffix}`}
                >
                  {name}
                </SpaceTypeNameContainer>
              )}
            </Box>
          </SpaceBetweenRow>
        </GridColumn>
        <GridColumn xs={1}>
          <WorksheetValuesInput
            startAdornment="1:"
            value={ratioValue}
            allowDecimals={showDecimals}
            groupSeparator=","
            disabled={disabled}
            onValueChange={(value) => {
              // isUpdatingWithRoundingPopup.current = false;
              handleInputChange(value, MAX_QTY);
            }}
            onBlur={() => {
              const newValues = onBlurRatio({
                row: {
                  ...row,
                  quantity: row.lockersOrDrawersQuantity,
                  ratio: row.ratio,
                },
                currentRatio: row.ratio,
                editValue,
                totalWorkSeats,
                isDirty,
                isRatio: true,
              });
              const { newRatio } = newValues;
              let { newQuantity } = newValues;

              if (isDirty) {
                newQuantity = row.dividendForRatio / newRatio;
              }

              if (!_.isFinite(newQuantity)) {
                newQuantity = 0;
              }
              const hasRowValueChanged =
                newQuantity !== row.lockersOrDrawersQuantity;
              if (hasRowValueChanged)
                changeChildRow(
                  WorksheetStorageSpaceRow.toWorksheetRow(
                    {
                      ...row,
                      ratio: newRatio,
                      lockersOrDrawersQuantity: newQuantity,
                    },
                    totalWorkSeats
                  )
                );
              blurCurrentInput();
            }}
            onFocus={() => onFocusRowInput('ratio')}
            onKeyPress={handleKeyPress}
            className="left-align"
            data-testid={`worksheet_row_ratio_${testingSuffix}`}
            data-cy={`worksheet_${
              row.child ? 'child_' : ''
            }row_ratio_${spaceTypeName}`}
          />
        </GridColumn>
        <GridColumn xs={1}>
          <WorksheetValuesInput
            value={quantityValue}
            inputRef={quantityInputRef}
            error={!isWholeNumber(row.lockersOrDrawersQuantity)}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            onFocus={(e) => {
              onFocusRowInput('lockersOrDrawersQuantity');
              if (
                !isWholeNumber(row.lockersOrDrawersQuantity) &&
                showDecimals
              ) {
                setRoundingPopupAnchorEl(e.currentTarget);
              }
            }}
            onValueChange={(value) => handleInputChange(value, MAX_QTY)}
            onBlur={() => {
              const { newQuantity, rowSeatCap } = onBlurQuantity({
                row,
                editValue,
                isDirty,
              });
              const hasRowValueChanged =
                newQuantity !== row.lockersOrDrawersQuantity;
              if (hasRowValueChanged)
                changeChildRow(
                  WorksheetStorageSpaceRow.toWorksheetRow(
                    {
                      ...row,
                      lockersOrDrawersQuantity: newQuantity,
                      seatCap: rowSeatCap,
                    },
                    totalWorkSeats
                  )
                );

              blurCurrentInput();
              setRoundingPopupAnchorEl(null);
            }}
            data-testid={`worksheet_row_qty_${testingSuffix}`}
            data-cy={`worksheet_${
              row.child ? 'child_' : ''
            }row_quantity_${spaceTypeName}`}
          />
          {roundingPopupAnchorEl && (
            <QuantityRoundingPopup
              anchorEl={roundingPopupAnchorEl}
              onRound={handleRoundQuantity}
            />
          )}
        </GridColumn>

        <GridColumn xs={1}>
          <div data-testid={`worksheet_row_unitshigh_${testingSuffix}`}>
            {numberWithCommas(row.lockersOrDrawersPerUnit, showDecimals, true)}
          </div>
        </GridColumn>
        <GridColumn
          xs={1}
          data-testid={`worksheet_row_rowtotal_${testingSuffix}`}
        >
          {numberWithCommas(row.quantity, showDecimals, true)}
        </GridColumn>
        <GridColumn
          xs={2}
          className="seat-percent-column nowrap"
          data-testid={`worksheet_row_seatpercent_${testingSuffix}`}
        >
          1:{numberWithCommas(row.storageUnitsRatio, showDecimals, true)}
        </GridColumn>
        <GridColumn xs={1}>
          <CenteredSpaceBetweenRow>
            <WorksheetValuesInput
              className="width60"
              disabled={disabled}
              value={sizeXValue}
              onKeyPress={handleKeyPress}
              onFocus={(e) => onFocusRowInput('sizeX', e)}
              onValueChange={(value) => handleInputChange(value, MAX_DIM)}
              onBlur={handleSizeInputBlurred}
              data-testid={`worksheet_row_sizex_${testingSuffix}`}
              data-cy={`worksheet_row_sizeX_${spaceTypeName}`}
            />
          </CenteredSpaceBetweenRow>
          <MultiplierBox>X</MultiplierBox>
        </GridColumn>

        <GridColumn xs={1} className="justifyCenter">
          <WorksheetValuesInput
            className="width60"
            disabled={disabled}
            value={sizeYValue}
            onKeyPress={handleKeyPress}
            onFocus={(e) => onFocusRowInput('sizeY', e)}
            onValueChange={(value) => handleInputChange(value, MAX_DIM)}
            onBlur={handleSizeInputBlurred}
            data-testid={`worksheet_row_sizey_${testingSuffix}`}
            data-cy={`worksheet_row_sizeY_${spaceTypeName}`}
          />
        </GridColumn>
        <GridColumn xs={1}>
          <WorksheetValuesInput
            error={shouldShowUSFError}
            className="usf-input"
            disabled={disabled}
            value={usableSquareInchesValue}
            onKeyPress={handleKeyPress}
            onFocus={(e) => onFocusRowInput('usableSquareInches', e)}
            onValueChange={(value) => handleInputChange(value, MAX_USF)}
            onBlur={() => {
              if (editValue === initialEditValue.current) {
                blurCurrentInput();
                return;
              }
              const { usf, sizeX, sizeY } = onBlurUsf(
                {
                  ...row,
                  usf: convertToUnit(row.usableSquareInches, {
                    unitType: 'area',
                    inputUnit: Unit.inches,
                    outputUnit:
                      measurementSystem === MeasurementSystem.imperial
                        ? Unit.inches
                        : Unit.meters,
                  }) as number,
                },
                usableSquareInchesValue,
                isDirty
              );

              const newUsf = convertToUnit(usf, {
                unitType: 'area',
                inputUnit:
                  measurementSystem === MeasurementSystem.imperial
                    ? Unit.inches
                    : Unit.meters,
                outputUnit: Unit.inches,
              }) as number;

              const hasRowValueChanged =
                newUsf !== row.usableSquareInches ||
                sizeX !== row.sizeX ||
                sizeY !== row.sizeY;
              if (hasRowValueChanged)
                changeChildRow(
                  WorksheetStorageSpaceRow.toWorksheetRow(
                    {
                      ...row,
                      usableSquareInches: newUsf,
                      sizeX,
                      sizeY,
                    },
                    totalWorkSeats
                  )
                );
              blurCurrentInput();
            }}
            data-testid={`worksheet_row_usf_${testingSuffix}`}
            data-cy={`worksheet_row_usf_${spaceTypeName}`}
          />
        </GridColumn>
        <GridColumn
          xs={1}
          className="row-end"
          data-testid="row-usf"
          data-cy={`worksheet_row_totalusf_${testingSuffix}`}
        >
          {convertToUnit(row.totalUsf, {
            unitType: 'area',
            formatter: (v) => numberWithCommas(v, showDecimals, true),
          })}
        </GridColumn>
      </GridRow>
    </div>
  );
};

export default StorageSpaceTypeRow;
